import { clsx } from 'clsx'
import { useCallback, useState } from 'react'
import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost'
type Size = 'sm' | 'md'

export function Button(
  props: ButtonHTMLAttributes<HTMLButtonElement> & {
    variant?: Variant
    size?: Size
    leftIcon?: ReactNode
  },
) {
  const variant = props.variant ?? 'primary'
  const size = props.size ?? 'md'
  const { className, leftIcon, onClick, disabled, ...rest } = props
  const [busy, setBusy] = useState(false)

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      if (!onClick || busy) return
      setBusy(true)
      try {
        await onClick(e)
      } finally {
        setBusy(false)
      }
    },
    [onClick, busy],
  )

  return (
    <button
      {...rest}
      disabled={disabled || busy}
      onClick={handleClick}
      className={clsx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition disabled:cursor-not-allowed disabled:opacity-60',
        size === 'md' && 'px-3 py-2 text-sm',
        size === 'sm' && 'px-2.5 py-1.5 text-xs',
        variant === 'primary' &&
          'bg-[var(--jnavi-navy)] text-white hover:brightness-110',
        variant === 'secondary' &&
          'border border-slate-200 bg-white text-slate-800 hover:bg-slate-50',
        variant === 'ghost' && 'text-slate-700 hover:bg-slate-100',
        className,
      )}
    >
      {leftIcon ? <span className="shrink-0">{leftIcon}</span> : null}
      {busy ? (
        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
      ) : null}
      {props.children}
    </button>
  )
}
